import { Component, effect, inject, input, output, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { startWith, take } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { SnackbarService } from 'ok-angular-shared/components';
import { Organization } from '../../../../organization-models';
import { AdmPriceExInclVatComponent } from 'src/app/features/prices/price-policies/components/price-ex-incl-vat/price-ex-incl-vat.component';
import { PriceRegulation, PriceRegulationType } from 'src/app/features/prices/price-regulations/price-regulation';
import { TranslatePriceRegulationTypePipe } from 'src/app/shared/pipes/translate-price-regulation-type.pipe';
import {
  CreatePriceRegulationRequest,
  EditDiscountPriceRegulationRequest,
  EditSupplementOverridePriceRegulationRequest,
  PriceRegulationService,
} from 'src/app/shared/services/price-regulation.service';
import { supplementOverrideValidator } from 'src/app/shared/validators/supplement-override.validator';

interface PriceRegulationCreateForm {
  name: FormControl<string>;
  type: FormControl<PriceRegulationType>;
  discountAmountInKrWithoutVat: FormControl<number | null>;
  overrideOkSupplement: FormControl<boolean>;
  okSupplementOverrideInKrWithoutVat: FormControl<number | null>;
  overrideOrganizationSupplement: FormControl<boolean>;
  organizationSupplementOverrideInKrWithoutVat: FormControl<number | null>;
}

@Component({
  selector: 'adm-org-wizard-price-regulations-create',
  templateUrl: './price-regulation-create.component.html',
  styleUrl: './price-regulation-create.component.scss',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatSlideToggle,
    AdmPriceExInclVatComponent,
    TranslatePriceRegulationTypePipe,
  ],
})
export class Step4PriceRegulationsCreateComponent {
  private priceRegulationService = inject(PriceRegulationService);
  private snackbarService = inject(SnackbarService);

  organization = input.required<Organization>();
  regulationCreated = output<void>();
  regulationEditClosed = output<void>();
  regulationToEdit = input<PriceRegulation | null>(null);

  public PriceRegulationType = PriceRegulationType;
  public showForm = signal(false);
  public hasCreatedRegulation = signal(false);
  public editMode = signal(false);

  public form = new FormGroup<PriceRegulationCreateForm>(
    {
      name: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(100), Validators.pattern(/.*\S.*/)],
      }),
      type: new FormControl<PriceRegulationType>(PriceRegulationType.Discount, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      discountAmountInKrWithoutVat: new FormControl<number | null>(null, {
        validators: [Validators.required, Validators.min(0), Validators.max(100)],
      }),
      overrideOkSupplement: new FormControl(false, { nonNullable: true }),
      okSupplementOverrideInKrWithoutVat: new FormControl<number | null>(null),
      overrideOrganizationSupplement: new FormControl(false, { nonNullable: true }),
      organizationSupplementOverrideInKrWithoutVat: new FormControl<number | null>(null),
    },
    { validators: supplementOverrideValidator(PriceRegulationType.SupplementOverride) }
  );

  public selectedType = toSignal(this.form.controls.type.valueChanges.pipe(startWith(this.form.controls.type.value)), {
    initialValue: this.form.controls.type.value,
  });

  constructor() {
    effect(() => {
      const selectedRegulation = this.regulationToEdit();
      if (!selectedRegulation) {
        return;
      }

      this.editMode.set(true);
      this.showForm.set(true);
      this.form.patchValue(
        {
          name: selectedRegulation.name,
          type: selectedRegulation.type,
          discountAmountInKrWithoutVat: selectedRegulation.discountAmountInKrWithoutVat,
          overrideOkSupplement: selectedRegulation.okSupplementOverrideInKrWithoutVat !== null,
          okSupplementOverrideInKrWithoutVat: selectedRegulation.okSupplementOverrideInKrWithoutVat,
          overrideOrganizationSupplement: selectedRegulation.organizationSupplementOverrideInKrWithoutVat !== null,
          organizationSupplementOverrideInKrWithoutVat: selectedRegulation.organizationSupplementOverrideInKrWithoutVat,
        },
        { emitEvent: true }
      );

      this.form.controls.type.disable({ emitEvent: false });
      this.form.markAsPristine();
    });

    this.form.controls.type.valueChanges.subscribe(() => {
      this.syncTypeControls();
    });
    this.form.controls.overrideOkSupplement.valueChanges.subscribe(enabled => {
      this.syncSupplementControl(this.form.controls.okSupplementOverrideInKrWithoutVat, enabled);
    });
    this.form.controls.overrideOrganizationSupplement.valueChanges.subscribe(enabled => {
      this.syncSupplementControl(this.form.controls.organizationSupplementOverrideInKrWithoutVat, enabled);
    });

    this.syncTypeControls();
  }

  public showCreateForm() {
    this.editMode.set(false);
    this.form.controls.type.enable({ emitEvent: false });
    this.showForm.set(true);
  }

  public cancelCreate() {
    this.form.reset({
      name: '',
      type: PriceRegulationType.Discount,
      discountAmountInKrWithoutVat: null,
      overrideOkSupplement: false,
      okSupplementOverrideInKrWithoutVat: null,
      overrideOrganizationSupplement: false,
      organizationSupplementOverrideInKrWithoutVat: null,
    });
    this.editMode.set(false);
    this.form.controls.type.enable({ emitEvent: false });
    this.syncTypeControls();
    this.showForm.set(false);
    if (this.regulationToEdit()) {
      this.regulationEditClosed.emit();
    }
  }

  public createPriceRegulation() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.getRawValue();
    const selectedType = formValue.type;

    if (selectedType === PriceRegulationType.Discount) {
      const discountValue = formValue.discountAmountInKrWithoutVat;
      if (discountValue == null) {
        this.form.controls.discountAmountInKrWithoutVat.markAsTouched();
        return;
      }

      if (this.editMode()) {
        const editRegulation = this.regulationToEdit();
        if (!editRegulation) {
          return;
        }

        const request: EditDiscountPriceRegulationRequest = {
          name: formValue.name,
          discount_amount_in_kr_without_vat: discountValue,
          note: null,
        };

        this.priceRegulationService
          .updatePriceRegulation(editRegulation.identifier, request)
          .pipe(take(1))
          .subscribe({
            next: () => this.handleSaveSuccess('Prisregulering opdateret.'),
            error: () => this.snackbarService.error('Der skete en ukendt fejl under opdatering af prisregulering.'),
          });
        return;
      }

      const request: CreatePriceRegulationRequest = {
        name: formValue.name,
        organization_id: this.organization().id,
        type: PriceRegulationType.Discount,
        discount_amount_in_kr_without_vat: discountValue,
        ok_supplement_override_in_kr_without_vat: null,
        organization_supplement_override_in_kr_without_vat: null,
        note: null,
      };

      this.priceRegulationService
        .createPriceRegulation(request)
        .pipe(take(1))
        .subscribe({
          next: () => this.handleSaveSuccess('Prisregulering oprettet.'),
          error: () => this.snackbarService.error('Der skete en ukendt fejl under oprettelse af prisregulering.'),
        });
      return;
    }

    const okSupplementValue = formValue.overrideOkSupplement ? formValue.okSupplementOverrideInKrWithoutVat : null;
    const organizationSupplementValue = formValue.overrideOrganizationSupplement
      ? formValue.organizationSupplementOverrideInKrWithoutVat
      : null;

    if (okSupplementValue == null && organizationSupplementValue == null) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.editMode()) {
      const editRegulation = this.regulationToEdit();
      if (!editRegulation) {
        return;
      }

      const request: EditSupplementOverridePriceRegulationRequest = {
        name: formValue.name,
        ok_supplement_override_in_kr_without_vat: okSupplementValue,
        organization_supplement_override_in_kr_without_vat: organizationSupplementValue,
        note: null,
      };

      this.priceRegulationService
        .updateSupplementOverridePriceRegulation(editRegulation.identifier, request)
        .pipe(take(1))
        .subscribe({
          next: () => this.handleSaveSuccess('Prisregulering opdateret.'),
          error: () => this.snackbarService.error('Der skete en ukendt fejl under opdatering af prisregulering.'),
        });
      return;
    }

    const request: CreatePriceRegulationRequest = {
      name: formValue.name,
      organization_id: this.organization().id,
      type: PriceRegulationType.SupplementOverride,
      discount_amount_in_kr_without_vat: null,
      ok_supplement_override_in_kr_without_vat: okSupplementValue,
      organization_supplement_override_in_kr_without_vat: organizationSupplementValue,
      note: null,
    };

    this.priceRegulationService
      .createPriceRegulation(request)
      .pipe(take(1))
      .subscribe({
        next: () => this.handleSaveSuccess('Prisregulering oprettet.'),
        error: () => this.snackbarService.error('Der skete en ukendt fejl under oprettelse af prisregulering.'),
      });
  }

  private handleSaveSuccess(message: string) {
    this.hasCreatedRegulation.set(true);
    this.snackbarService.success(message);
    this.regulationCreated.emit();
    this.cancelCreate();
  }

  private syncTypeControls(): void {
    if (this.form.controls.type.value === PriceRegulationType.Discount) {
      this.form.controls.discountAmountInKrWithoutVat.enable({ emitEvent: false });
      this.form.controls.discountAmountInKrWithoutVat.setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
      this.form.controls.discountAmountInKrWithoutVat.updateValueAndValidity({ emitEvent: false });

      this.form.controls.overrideOkSupplement.disable({ emitEvent: false });
      this.form.controls.overrideOrganizationSupplement.disable({ emitEvent: false });

      this.form.controls.okSupplementOverrideInKrWithoutVat.setValue(null, { emitEvent: false });
      this.form.controls.okSupplementOverrideInKrWithoutVat.clearValidators();
      this.form.controls.okSupplementOverrideInKrWithoutVat.disable({ emitEvent: false });
      this.form.controls.okSupplementOverrideInKrWithoutVat.updateValueAndValidity({ emitEvent: false });

      this.form.controls.organizationSupplementOverrideInKrWithoutVat.setValue(null, { emitEvent: false });
      this.form.controls.organizationSupplementOverrideInKrWithoutVat.clearValidators();
      this.form.controls.organizationSupplementOverrideInKrWithoutVat.disable({ emitEvent: false });
      this.form.controls.organizationSupplementOverrideInKrWithoutVat.updateValueAndValidity({ emitEvent: false });
      return;
    }

    this.form.controls.discountAmountInKrWithoutVat.setValue(null, { emitEvent: false });
    this.form.controls.discountAmountInKrWithoutVat.clearValidators();
    this.form.controls.discountAmountInKrWithoutVat.disable({ emitEvent: false });
    this.form.controls.discountAmountInKrWithoutVat.updateValueAndValidity({ emitEvent: false });

    this.form.controls.overrideOkSupplement.enable({ emitEvent: false });
    this.form.controls.overrideOrganizationSupplement.enable({ emitEvent: false });

    this.syncSupplementControl(this.form.controls.okSupplementOverrideInKrWithoutVat, this.form.controls.overrideOkSupplement.value);
    this.syncSupplementControl(
      this.form.controls.organizationSupplementOverrideInKrWithoutVat,
      this.form.controls.overrideOrganizationSupplement.value
    );
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  private syncSupplementControl(control: FormControl<number | null>, enabled: boolean): void {
    if (enabled) {
      control.enable({ emitEvent: false });
      control.setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
    } else {
      control.setValue(null, { emitEvent: false });
      control.clearValidators();
      control.disable({ emitEvent: false });
    }

    control.updateValueAndValidity({ emitEvent: false });
    this.form.updateValueAndValidity({ emitEvent: false });
  }
}
